import React from "react";
import styled from "styled-components";
import { HTMLMotionProps, motion } from "framer-motion";
import { DefaultTheme } from "styled-components";

export interface FlexProps 
  extends HTMLMotionProps<"div"> { 
  className?: string;
  children: React.ReactNode;
  direction?: "row" | "column";
  justify?: "flex-start" | "center" | "flex-end" | "space-between" | "space-around" | "soft" | "elevated" | "outline" | "glass";
  align?: "flex-start" | "center" | "flex-end" | "stretch";
  gap?: keyof DefaultTheme["spacing"];
  wrap?: "nowrap" | "wrap" | "wrap-reverse";
  fullWidth?: boolean;
  fullHeight?: boolean;
  responsive?: boolean;
  maxWidth?: string; // Prevent excessive width on large screens
  variant?: "standard" | "card" | "list" | "bubble"; // UI variants
  shouldAnimate?: boolean; // ✅ Renamed to avoid conflict

  /** ✨ NEW PROPS */
  background?: string | ((theme: DefaultTheme) => string);
  hoverBackground?: string; // ✅ Background on hover
  padding?: keyof DefaultTheme["spacing"];
  margin?: keyof DefaultTheme["spacing"];
  borderRadius?: keyof DefaultTheme["radii"];
  borderColor?: keyof DefaultTheme["colors"];
  shadow?: keyof DefaultTheme["elevations"];
  hoverShadow?: keyof DefaultTheme["elevations"];
  opacity?: number;
  flex?: string; // ✅ Allows setting custom `flex` values like "1", "2", "0 1 auto"
  onHover?: () => void; // ✅ Hover callback for **prefetching, animations, analytics**
    style?: React.CSSProperties; // ✅ Support inline styles
    disableHoverEffect?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>; // ✅ Add this line
  $swipeDirection?: "left" | "right" | null; 
}

const StyledFlex = styled.div<FlexProps>`
  display: flex;
  flex: ${({ flex }) => (typeof flex === "number" ? `${flex} 1 auto` : flex || "1 1 auto")}; /* ✅ Ensure flex values work correctly for numeric values */
  flex-direction: ${({ direction }) => direction || "row"};
  justify-content: ${({ justify }) => justify || "flex-start"};
  align-items: ${({ align }) => align || "stretch"};
  flex-wrap: ${({ wrap }) => wrap || "nowrap"};
  gap: ${({ theme, gap }) => (gap ? theme.spacing[gap] : "0px")};
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
  height: ${({ fullHeight }) => (fullHeight ? "100%" : "auto")};
  max-width: ${({ maxWidth }) => maxWidth || "none"};

  /** ✅ Ensure Flex Shrinks/Grows Properly */
  min-height: 0;
  min-width: 0;

  /** ✅ Background Handling */
background: ${({ background, theme }) =>
    typeof background === "function" ? background(theme) : background || "transparent"};

  /** ✅ Padding, Margin, Border */
  padding: ${({ theme, padding }) => (padding ? theme.spacing[padding] : "0")};
  margin: ${({ theme, margin }) => (margin ? theme.spacing[margin] : "0")};
  border-radius: ${({ theme, borderRadius }) => (borderRadius ? theme.radii[borderRadius] : "0")};
  border: ${({ theme, borderColor }) => (borderColor ? `1px solid ${theme.colors[borderColor]}` : "none")};

  /** ✅ Shadow & Opacity */
  box-shadow: ${({ theme, shadow }) => (shadow ? theme.elevations[shadow] : "none")};
  opacity: ${({ opacity }) => (opacity !== undefined ? opacity : 1)};

  /** ✅ Hover Effects */
  transition: all 0.3s ease-in-out;
  ${({ disableHoverEffect, theme, hoverBackground, hoverShadow }) =>
    !disableHoverEffect &&
    `
    &:hover {
      background: ${hoverBackground || "inherit"};
      box-shadow: ${hoverShadow ? theme.elevations[hoverShadow] : "none"};
    }
  `}


  /** 📌 Responsive Adjustments */
  ${({ responsive, theme }) =>
    responsive &&
    `
    @media (max-width: ${theme.breakpoints.ultraWide}) {
      flex-direction: row;
      justify-content: space-between;
    }

    @media (max-width: ${theme.breakpoints.desktop}) {
      flex-direction: row;
      justify-content: space-between;
    }

    @media (max-width: ${theme.breakpoints.laptop}) {
      flex-direction: column;
      align-items: center;
    }

    @media (max-width: ${theme.breakpoints.tablet}) {
      flex-direction: column;
      width: 100%;
      align-items: center;
    }

    @media (max-width: ${theme.breakpoints.mobile}) {
      flex-direction: column;
      width: 100%;
      align-items: center;
    }
  `}
    /** 📌 VARIANT STYLES */
  ${({ variant, theme }) => {
    switch (variant) {
      case "card":
        return `
          padding: ${theme.spacing.md};
          border-radius: ${theme.radii.md};
          box-shadow: ${theme.elevations.low};
        `;
      case "list":
        return `
          flex-direction: column;
          gap: ${theme.spacing.md};
          padding: ${theme.spacing.md};
        `;
      case "bubble":
        return `
          // background: ${theme.colors.primary};
          // color: ${theme.colors.buttonText};
          padding: ${theme.spacing.sm} ${theme.spacing.md};
          border-radius: ${theme.radii.lg};
          max-width: 60%;
        `;
      default:
        return `
          border-radius: 0;
          box-shadow: 0;
        `;
    }
  }}
`;

export const Flex: React.FC<FlexProps> = ({
  children,
  shouldAnimate,
  className,
  onHover,
  onClick,
  style,
  disableHoverEffect=true,
  ...props
}) => {
  const Wrapper = shouldAnimate ? motion.div : "div";

  return (
    
      <StyledFlex 
      as={shouldAnimate ? motion.div : "div"} // ✅ Use `as` to dynamically set motion.div
      initial={shouldAnimate ? { opacity: 0, y: 10 } : undefined}
      animate={shouldAnimate ? { opacity: 1, y: 0 } : undefined}
      transition={shouldAnimate ? { duration: 0.3, ease: "easeOut" } : undefined}
      className={className}
      onMouseEnter={onHover}
      onClick={onClick} 

      style={style}
      disableHoverEffect={disableHoverEffect}
      {...props}
      >
        {children}
      </StyledFlex>
   
  );
};